import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'sonner';
import { apiService } from '../../../../utils/api';

export interface Manufacturer {
  id: string;
  title: string;
  url: string | null;
  company: string;
  is_active: boolean;
}

export interface ManufacturerPayload {
  title: string;
  url?: string | null;
}

const BASE = '/api/v1/manufacturer/';
export const MANUFACTURER_KEY = 'manufacturer';

const showError = (err: any) => {
  toast.error(err?.response?.data?.message || 'Operace se nezdařila');
};

export const useManufacturerList = (search?: string) =>
  useQuery<Manufacturer[]>({
    queryKey: [MANUFACTURER_KEY, 'list', search],
    queryFn: async () => {
      const { data } = await apiService.get(BASE, {
        params: { search: search || undefined },
      });
      return data;
    },
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

export const useManufacturerDetail = (id?: string | null) =>
  useQuery<Manufacturer>({
    queryKey: [MANUFACTURER_KEY, 'detail', id],
    queryFn: async () => {
      const { data } = await apiService.get(`${BASE}${id}/`);
      return data;
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
  });

export const useManufacturerDropdown = () =>
  useQuery<Manufacturer[]>({
    queryKey: [MANUFACTURER_KEY, 'dropdown'],
    queryFn: async () => {
      const { data } = await apiService.get(`${BASE}dropdown/`, {
        params: { pagination: false },
      });
      return data;
    },
    refetchOnWindowFocus: false,
  });

export const useCreateManufacturer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: ManufacturerPayload) => apiService.post<Manufacturer>(BASE, values),
    onSuccess: () => {
      queryClient.invalidateQueries(MANUFACTURER_KEY);
      toast.success('Výrobce vytvořen');
    },
    onError: showError,
  });
};

export const useUpdateManufacturer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values }: { id: string; values: Partial<ManufacturerPayload> }) =>
      apiService.patch<Manufacturer>(`${BASE}${id}/`, values),
    onSuccess: () => {
      queryClient.invalidateQueries(MANUFACTURER_KEY);
      toast.success('Výrobce upraven');
    },
    onError: showError,
  });
};

export const useToggleManufacturerActive = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      apiService.post(`${BASE}${id}/${isActive ? 'deactivate' : 'activate'}/`, {}),
    onSuccess: (_, { isActive }) => {
      queryClient.invalidateQueries(MANUFACTURER_KEY);
      toast.success(isActive ? 'Výrobce deaktivován' : 'Výrobce aktivován');
    },
    onError: showError,
  });
};
